import { webSocket, WebSocketSubject } from "rxjs/webSocket"
import { NextObserver } from "rxjs/internal/types";
import { retryWhen, delayWhen } from "rxjs/operators";
import { timer } from "rxjs";

/**
 * Manages a connection to a given WebSocket. Will automatically try to reconnect after losing a connection.
 * 
 * Templated class, where the provided template describes the type of packets allowed over the web socket.
 */
class WebSocketManager<T> {
    private deviceWebSocket: WebSocketSubject<T>;
    private connectionTimeout: number;
    private connected: boolean = false;

    /**
     * Constructs a WebSocketManager.
     * 
     * @param url URL of end host.
     * @param connectionTimeout Timeout in milliseconds to wait before attempting to re-establish connection.
     * @param socketObserver Observer for the web socket.
     */
    constructor(url: string, connectionTimeout: number, socketObserver: NextObserver<T>) {
        this.deviceWebSocket = webSocket({
            url,
            openObserver: { next: () => { this.connected = true } },
            closeObserver: { next: () => { this.connected = false } }
        });
        this.connectionTimeout = connectionTimeout;

        // Create observer and reconnect on error
        this.deviceWebSocket.pipe(retryWhen(errors => {
            return errors.pipe(delayWhen(() => timer(this.connectionTimeout))
            )
        })).subscribe(socketObserver);
    }

    /**
     * Connect to a WebSocket at the given URL.
     * 
     * @param url URL to connect to.
     */
    connect(url: string) {
        this.deviceWebSocket = webSocket(url);
    }

    /**
     * Send a packet over the Websocket.
     * 
     * @param packet Packet to send over Websocket.
     */
    send_packet(packet: T) {
        this.deviceWebSocket.next(packet);
    }

    /**
     * Retrieves the connection status of the WebSockets.
     * 
     * @returns connection status
     */
    is_connected() {
        return this.connected;
    }

}

export default WebSocketManager;